// Components==============
import indexCategories from 'actions/category/index';
import indexTrainingSessions from 'actions/training/session/index';
import indexTrainingSubscription from 'actions/training/subscription/index';
import storeTrainingSubscription from 'actions/training/subscription/store';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import NoContent from 'components/UI/NoContent/NoContent';
import SessionCard from 'components/UI/SessionCard/SessionCard';
import Summary from 'components/UI/Summary/Summary';
import TrainingLayout from 'components/UI/TrainingLayout/TrainingLayout';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { GlobalTrainingUserContext } from 'utilities/GlobalContext/GlobalTrainingUser';
import LessonCard from '../../../components/UI/LessonCard/LessonCard';
import Menu from './components/Menu/Menu';
import Body from './components/Summary/Body/Body';
import TopLeft from './components/Summary/TopLeft/TopLeft';
import TopRight from './components/Summary/TopRight/TopRight';
import Top from './components/Top/Top';
import {
  TrainingDetailFunctions,
  useTrainingDetailsFunctions,
} from './components/useTrainingDetailsFunctions';
import './TrainingDetails.module.scss';
// =========================

export type TrainingDetailsContextType = {
  training?: TrainingEntity;
  subscriber?: TrainingSubscriptionEntity | undefined;
  subscribers?: (TrainingSubscriptionEntity | undefined)[];
  sessionId?: number;
  session?: TrainingSessionEntity;
  loading?: boolean;
};

export const TrainingDetailsContext = createContext(
  {} as TrainingDetailsContextType & TrainingDetailFunctions
);

export default function TrainingDetails() {
  const dispatch = useReduxAction();
  const history = useHistory();

  const { trainingId, sessionId } = useParams() as {
    trainingId: string;
    sessionId?: string;
  };

  const { trainingUser, setTrainingUser, shouldResetTrainingUser } = useContext(
    GlobalTrainingUserContext
  );

  const userId = useRedux((state) => state.authentication?.user?.id);

  const training = useRedux(
    (state) => state.training?.entities.byId[trainingId]
  );

  const session = useRedux((state) =>
    sessionId ? state.trainingSession?.entities.byId[sessionId] : undefined
  );

  const subscriber = useRedux((state) =>
    state.trainingSubscription?.entities.allIds
      .map((id) => state.trainingSubscription?.entities.byId[id])
      .find(
        (s) => s?.user_id === trainingUser && `${s?.training_id}` === trainingId
      )
  );

  const subscribers = useRedux((state) =>
    state.trainingSubscription?.entities.allIds
      .map((id) => state.trainingSubscription?.entities.byId[id])
      .filter(
        (s) =>
          s?.user_id !== userId &&
          s?.used_sharecode &&
          `${s?.training_id}` === trainingId
      )
  );

  useEffect(() => {
    if (shouldResetTrainingUser && userId) setTrainingUser(userId);
  }, []);

  useEffect(() => {
    dispatch(indexTraining());
    dispatch(indexTrainingSubscription({ trainingId })).then(
      (res: TrainingSubscriptionEntity[]) => {
        if (!res.length || res.find((r) => r.user_id === userId) === undefined)
          return dispatch(storeTrainingSubscription({ trainingId }));
      }
    );
    dispatch(indexTrainingSessions({ trainingId }));
    dispatch(indexCategories());
  }, []);

  const loading = !!useRedux(
    (state) =>
      state.training?.loading ||
      state.trainingSubscription?.loading ||
      state.trainingSession?.loading ||
      state.category?.loading ||
      state.skill?.loading
  );

  const [isOpen, setIsOpen] = useState(false);

  const images = training?.attachments
    ?.filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path);

  const contextValues = {
    training,
    subscriber,
    subscribers,
    session,
    sessionId: sessionId ? parseInt(sessionId) : undefined,
    loading,
  };

  const functions = useTrainingDetailsFunctions(contextValues);

  return (
    <Layout grey>
      <TrainingDetailsContext.Provider
        value={{ ...contextValues, ...functions }}
      >
        <Summary
          type="training"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          topLeftContent={<TopLeft training={training} isOpen={isOpen} />}
          topRightContent={<TopRight training={training} />}
          bodyContent={<Body training={training} />}
          images={images}
          video={training?.youtube}
        />
        <NoContent
          page="training"
          loading={false}
          hasContent={
            training?.sessions ? training?.sessions.length > 0 : false
          }
          decreasedHeight={70}
          handleClick={() =>
            history.push(`/werkplaats/training-bouwer/${training?.id}`)
          }
        >
          <div styleName="container">
            <Top />
            <TrainingLayout
              content={
                <div styleName="content-wrapper">
                  <SessionCard
                    session={session}
                    sessionId={sessionId ? parseInt(sessionId) : undefined}
                    training={training}
                    subscriber={subscriber}
                  />
                  {session?.lessons
                    ?.sort(
                      (a, b) => (a.order_index || 0) - (b.order_index || 0)
                    )
                    .map((lesson, index) => (
                      <LessonCard
                        lesson={lesson}
                        key={lesson.id}
                        index={index}
                        subscriber={subscriber}
                        toggleDone={functions.toggleDone}
                        handleEvidenceUpload={functions.handleEvidenceUpload}
                        handleEvidenceDelete={functions.handleEvidenceDelete}
                      />
                    ))}
                </div>
              }
              menu={<Menu />}
            />
          </div>
        </NoContent>
      </TrainingDetailsContext.Provider>
    </Layout>
  );
}
